import { DateTime } from '@ksyos/date-time';
import { Alert } from '@ksyos/design-system';
import type { ValidationErrors } from '../../../../../utils/validation';
import type { IServiceGetOne } from '../../../../interfaces/serviceGetOne';
import type { ICustomControlData } from './TiecgV1RequestAddEdit';
import { Gender } from '../../../../../../common/enums/gender';
import { ProcessEntity } from '../../../../../../common/enums/processEntity';
import {
    cardioInspanningsEcgRequestCriteriaForProceedingSchema,
    cardioInspanningsEcgRequestCriteriaSchema,
    contraIndicatieOptions,
} from '../../../../../../common/validation/customControls/cardiology/holterConsult/cardiology';
import { fetchJson } from '../../../../../store/fetch';
import { translateKey } from '../../../../../translate';
import { emptyValidationErrors, updateValidationErrorsForOne, validate } from '../../../../../utils/validation';
import { CancelButton } from '../../../../elements/carePathButtons/CancelButton';
import {
    AnchorLink,
    Button,
    ButtonContainer,
    FormDivider,
    HeaderDivider,
    Icon,
    IconColor,
    InputCheckbox,
    InputNumber,
    InputRadioGroup,
    ThirdHeader,
} from '../../../../elements/common';
import { Component } from '../../../../elements/Component';
import { FormLabel } from '../../../../elements/forms/FormLabel';
import { HorizontalFormGroup } from '../../../../elements/forms/HorizontalFormGroup';
import { InputContainer } from '../../../../elements/forms/InputContainer';
import { calculateCoronairSclerosePercentage } from './calculateCoronairSclerosePercentage';

interface IRequestCriteriaProps {
    customControlData: ICustomControlData;
    handleChange: (customControlData: Partial<ICustomControlData>) => Promise<void>;
    hideCriteria: (hide: boolean) => Promise<void>;
    onCancel: () => Promise<void>;
    patientId: string;
    service: IServiceGetOne;
}

interface IRequestCriteriaState {
    errors: ValidationErrors;
    errorsForProceeding: ValidationErrors;
    indicationOutcomeText?: string;
    patient: {
        age: number;
        gender: Gender;
    };
    submitButtonIsDisabled: boolean;
}

export interface IRequestCriteriaData {
    bloeddruk_diastolisch: number | null;
    bloeddruk_systolisch: number | null;
    contra_indicaties: string | null;
    coronairSclerosePercentage: number;
    provocatie_klachten: boolean;
    retrosternale_pijnklachten: boolean;
    verdwijning_klachten: boolean;
}

const tooltipText = (
    <ul>
        <li className="listheader">Is minstens één van de volgende contra-indicaties aanwezig?</li>
        <li>Vermoeden acuut myocardinfarct (volg spoedprocedure)</li>
        <li>Vermoeden instabiele AP (volg spoedprocedure)</li>
        <li>Manifest hartfalen</li>
        <li>Ernstige aortaklepstenose</li>
        <li>Hypertrofische cardiomyopathie</li>
        <li>Aneurysma aortae</li>
        <li>Onder controle bij cardioloog</li>
        <li>LVH met repolarisatiestoornissen op het Rust ECG</li>
        <li>Bekende afwijkingen op het rust ECG, zoals ST depressie of LBTB</li>
        <li>Draagt een pacemaker</li>
        <li>Recente pulmonale embolie of trombose</li>
        <li>
            Lichamelijke en/of verstandelijke beperking(en) waardoor inspanning en/of instructie niet (goed) mogelijk is
        </li>
        <li>Koorts (temperatuur boven 38 graden)</li>
    </ul>
);

export class RequestCriteria extends Component<IRequestCriteriaProps, IRequestCriteriaState> {
    constructor(props: IRequestCriteriaProps) {
        super(props);

        this.state = {
            patient: {
                age: 0,
                gender: Gender.unknown,
            },
            errors: emptyValidationErrors,
            errorsForProceeding: emptyValidationErrors,
            submitButtonIsDisabled: true,
        };
    }

    public async componentDidMount() {
        await this.loadPatient();
        await this.validateForProceeding();
        await this.handleCoronairSclerosePercentage();
    }

    public render() {
        const customControlData = this.props.customControlData.requestCriteriaData;

        return (
            <>
                <HeaderDivider
                    display="inline-block"
                    text={'Bepaal de kans op belangrijke coronairsclerose'}
                />
                <AnchorLink
                    openInNewTab
                    paddingDirection="l"
                    paddingValue={2}
                    to="/assets/documents/A_clinical_prediction_rule_for_the_diagnosis_of_coronary_artery_disease_validation_updating_and_extension.pdf"
                >
                    <Icon
                        color={IconColor.secondary}
                        icon="external"
                    />
                    Bron: Genders (2011)
                </AnchorLink>

                <HorizontalFormGroup
                    hasErrors={
                        this.state.errors.has('retrosternale_pijnklachten') ||
                        this.state.errors.has('provocatie_klachten') ||
                        this.state.errors.has('verdwijning_klachten')
                    }
                >
                    <FormLabel>Symptomen</FormLabel>
                    <InputContainer>
                        <InputCheckbox
                            checked={customControlData.retrosternale_pijnklachten}
                            error={this.state.errors.get('retrosternale_pijnklachten')}
                            onChange={async (value) => await this.handleChange('retrosternale_pijnklachten', value)}
                            onDoValidate={async () => await this.validateOne('retrosternale_pijnklachten')}
                            text={'Retrosternale pijnklachten'}
                        />
                        <InputCheckbox
                            checked={customControlData.provocatie_klachten}
                            error={this.state.errors.get('provocatie_klachten')}
                            onChange={async (value) => await this.handleChange('provocatie_klachten', value)}
                            onDoValidate={async () => await this.validateOne('provocatie_klachten')}
                            text={
                                'Provocatie van de klachten door inspanning, kou, warmte, emoties of zware maaltijden'
                            }
                        />
                        <InputCheckbox
                            checked={customControlData.verdwijning_klachten}
                            error={this.state.errors.get('verdwijning_klachten')}
                            onChange={async (value) => await this.handleChange('verdwijning_klachten', value)}
                            onDoValidate={async () => await this.validateOne('verdwijning_klachten')}
                            text={
                                'Verdwijning van de klachten binnen 15 minuten in rust en/of binnen enkele minuten na het gebruik van sublinguaal toegediende nitraten.'
                            }
                        />
                    </InputContainer>
                </HorizontalFormGroup>

                <ThirdHeader
                    status="warning"
                    text={`${this.props.customControlData.indicationOutcomeText || ''}`}
                />
                <FormDivider />
                <HeaderDivider text={'Contra-indicatie'} />

                <HorizontalFormGroup hasErrors={this.state.errors.has('contra_indicaties')}>
                    <FormLabel
                        isRequired
                        tooltipText={tooltipText}
                    >
                        Contra-indicatie(s) aanwezig?
                    </FormLabel>
                    <InputContainer>
                        <InputRadioGroup
                            automation="contraIndicaties"
                            error={this.state.errors.get('contra_indicaties')}
                            name="contra_indicaties"
                            onChange={async (value) => await this.handleChange('contra_indicaties', value)}
                            onDoValidate={async () => await this.validateOne('contra_indicaties')}
                            options={contraIndicatieOptions}
                            value={customControlData.contra_indicaties}
                        />
                    </InputContainer>
                </HorizontalFormGroup>

                <HorizontalFormGroup hasErrors={this.state.errors.has('bloeddruk_systolisch')}>
                    <FormLabel isRequired>Bloeddruk systolisch</FormLabel>
                    <div className="col-4">
                        <InputNumber
                            error={this.state.errors.get('bloeddruk_systolisch')}
                            onChange={async (value) => {
                                await this.handleChange('bloeddruk_systolisch', value);
                                await this.validateForProceeding();
                            }}
                            onDoValidate={async () => await this.validateOne('bloeddruk_systolisch')}
                            text={'mmHg'}
                            value={customControlData.bloeddruk_systolisch}
                        />
                    </div>
                </HorizontalFormGroup>

                <HorizontalFormGroup hasErrors={this.state.errors.has('bloeddruk_diastolisch')}>
                    <FormLabel isRequired>Bloeddruk diastolisch</FormLabel>
                    <div className="col-4">
                        <InputNumber
                            error={this.state.errors.get('bloeddruk_diastolisch')}
                            onChange={async (value) => {
                                await this.handleChange('bloeddruk_diastolisch', value);
                                await this.validateForProceeding();
                            }}
                            onDoValidate={async () => await this.validateOne('bloeddruk_diastolisch')}
                            text={'mmHg'}
                            value={customControlData.bloeddruk_diastolisch}
                        />
                    </div>
                </HorizontalFormGroup>
                {this.getWarningMessages()}
                <ButtonContainer
                    alignRight
                    buttons={[
                        <CancelButton
                            cancel={this.props.onCancel}
                            entity={ProcessEntity.request}
                            serviceCode={this.props.service.code}
                            serviceType={this.props.service.serviceType}
                        />,
                        <Button
                            color="secondary"
                            disabled={this.isBlocked()}
                            large
                            onClick={async () => await this.save()}
                        >
                            {translateKey('common.proceed')}
                        </Button>,
                    ]}
                />
            </>
        );
    }

    private async handleChange<K extends keyof IRequestCriteriaData>(name: K, value: IRequestCriteriaData[K]) {
        await this.props.handleChange({
            requestCriteriaData: { ...this.props.customControlData.requestCriteriaData, [name]: value },
        });
        await this.handleCoronairSclerosePercentage();
    }

    private async handleCoronairSclerosePercentage() {
        const numberOfIndications = Object.values(this.props.customControlData.requestCriteriaData).filter(
            (checkboxValue) => checkboxValue === true
        ).length;
        const { coronairSclerosePercentage, indicationOutcomeText } = calculateCoronairSclerosePercentage(
            this.state.patient.age,
            this.state.patient.gender,
            numberOfIndications
        );

        await this.props.handleChange({
            indicationOutcomeText,
            requestCriteriaData: { ...this.props.customControlData.requestCriteriaData, coronairSclerosePercentage },
        });
    }

    private async validateOne(name: keyof IRequestCriteriaData) {
        await this.setState(({ errors }) => ({
            errors: updateValidationErrorsForOne(
                cardioInspanningsEcgRequestCriteriaSchema,
                this.props.customControlData.requestCriteriaData,
                errors,
                name
            ),
        }));
        await this.validateForProceeding();
    }

    private async validateForProceeding() {
        const { errors } = validate(
            cardioInspanningsEcgRequestCriteriaForProceedingSchema,
            this.props.customControlData.requestCriteriaData
        );
        await this.setState({ errorsForProceeding: errors });
    }

    private getWarningMessages() {
        const warningArray = [];
        if (this.state.errorsForProceeding.get('contra_indicaties') === 'any_allowOnly') {
            warningArray.push(
                <Alert
                    appearance="warning"
                    key={1}
                >
                    Wegens 1 of meer contra-indicaties komt deze patiënt niet in aanmerking voor een inspanning ECG
                    onderzoek.
                </Alert>
            );
        }

        if (
            this.state.errorsForProceeding.get('coronairSclerosePercentage') === 'number_min' ||
            this.state.errorsForProceeding.get('coronairSclerosePercentage') === 'number_max'
        ) {
            warningArray.push(
                <Alert
                    appearance="warning"
                    key={2}
                >
                    Alleen bij een intermediaire kans (30-70%) op belangrijke coronairsclerose komt deze patiënt in
                    aanmerking voor een Inspannings ECG.
                </Alert>
            );
        }

        if (
            this.state.errorsForProceeding.get('bloeddruk_systolisch') === 'number_max' ||
            this.state.errorsForProceeding.get('bloeddruk_diastolisch') === 'number_max'
        ) {
            warningArray.push(
                <Alert
                    appearance="warning"
                    key={3}
                >
                    De bloeddruk is te hoog om een Inspannings ECG af te nemen bij deze patiënt
                </Alert>
            );
        }

        return warningArray;
    }

    private isBlocked() {
        return (
            this.state.errorsForProceeding.get('contra_indicaties') === 'any_allowOnly' ||
            this.state.errorsForProceeding.get('coronairSclerosePercentage') === 'number_max'
        );
    }

    private async loadPatient() {
        const result = await fetchJson(`/api/patients/${this.props.patientId}`, 'GET');
        await this.setState({
            patient: {
                age: DateTime.now().diff(result.json.birthDate, 'years'),
                gender: result.json.gender,
            },
        });
    }

    private async save() {
        const { hasErrors, errors } = validate(
            cardioInspanningsEcgRequestCriteriaSchema,
            this.props.customControlData.requestCriteriaData
        );
        await this.setState({ errors });
        if (hasErrors) {
            return;
        }
        await this.validateForProceeding();
        if (this.isBlocked()) {
            return;
        }

        await this.props.hideCriteria(true);
    }
}
